<template>
  <div v-if="open" class="overlay" role="presentation" @click.self="$emit('cancel')">
    <section class="dialog" role="dialog" aria-modal="true" aria-labelledby="auto-heading">
      <header><strong id="auto-heading">Enable automatic trading?</strong><button type="button" aria-label="Close" @click="$emit('cancel')">×</button></header>
      <p class="warning">Live trades will execute with real SOL from the selected wallet. Market moves, fees and slippage may cause losses.</p>
      <div class="explain"><strong>How it works</strong><p>Strategies evaluate live market signals. Platform guards validate each trade intent before execution. Position exits follow your configured strategy and risk settings. You can turn AUTO off at any time.</p></div>
      <label for="auto-delay">Delay before automatic buy</label>
      <div class="stepper">
        <button type="button" aria-label="Decrease delay by 100 milliseconds" :disabled="busy || delay <= 0" @click="delay = Math.max(0, delay - 100)">−</button>
        <output id="auto-delay">{{ (delay / 1000).toFixed(2) }} s</output>
        <button type="button" aria-label="Increase delay by 500 milliseconds" :disabled="busy || delay >= 600000" @click="delay = Math.min(600000, delay + 500)">+</button>
      </div>
      <small>− 100 ms · + 500 ms · Maximum 10 min</small>

      <div class="breaker">
        <div class="breaker-heading">
          <div>
            <strong id="auto-breaker-label">Automatic circuit breaker</strong>
            <span class="breaker-state">{{ breakerEnabled ? 'On' : 'Off' }}</span>
          </div>
          <button type="button" class="switch" role="switch" :aria-checked="breakerEnabled" aria-labelledby="auto-breaker-label" :disabled="busy" @click="toggleBreaker">
            <span class="switch-thumb" />
          </button>
        </div>
        <p>When a limit is reached, AUTO stops opening new positions. Open positions keep their protective exits.</p>
      </div>

      <div v-if="breakerEnabled" class="breaker-controls">
        <label for="auto-max-positions">Stop after AUTO entries</label>
        <div class="stepper">
          <button type="button" aria-label="Decrease maximum AUTO entries by one" :disabled="busy || maxPositions <= 0" @click="maxPositions = Math.max(0, maxPositions - 1)">−</button>
          <output id="auto-max-positions">{{ maxPositions === 0 ? 'Unlimited' : maxPositions }}</output>
          <button type="button" aria-label="Increase maximum AUTO entries by one" :disabled="busy || maxPositions >= 1000" @click="maxPositions = Math.min(1000, maxPositions + 1)">+</button>
        </div>
        <small>− 1 · + 1 · 0 = unlimited · Maximum 1000</small>

        <label for="auto-target-profit">Stop at confirmed session profit</label>
        <div class="stepper">
          <button type="button" aria-label="Decrease profit target by 0.001 SOL" :disabled="busy || profitLamports <= 0" @click="profitLamports = Math.max(0, profitLamports - solStep)">−</button>
          <output id="auto-target-profit">{{ solLabel(profitLamports) }}</output>
          <button type="button" aria-label="Increase profit target by 0.001 SOL" :disabled="busy || profitLamports >= maxSolLamports" @click="profitLamports = Math.min(maxSolLamports, profitLamports + solStep)">+</button>
        </div>
        <small>− 0.001 SOL · + 0.001 SOL · 0 = off</small>

        <label for="auto-max-loss">Stop at confirmed session loss</label>
        <div class="stepper">
          <button type="button" aria-label="Decrease loss limit by 0.001 SOL" :disabled="busy || lossLamports <= 0" @click="lossLamports = Math.max(0, lossLamports - solStep)">−</button>
          <output id="auto-max-loss">{{ solLabel(lossLamports) }}</output>
          <button type="button" aria-label="Increase loss limit by 0.001 SOL" :disabled="busy || lossLamports >= maxSolLamports" @click="lossLamports = Math.min(maxSolLamports, lossLamports + solStep)">+</button>
        </div>
        <small>− 0.001 SOL · + 0.001 SOL · 0 = off</small>
        <small class="breaker-note">The first reached limit disables new AUTO entries. Profit and loss use confirmed closed-position results.</small>
      </div>
      <p v-if="error" class="error" role="alert">{{ error }}</p>
      <footer><button type="button" :disabled="busy" @click="$emit('cancel')">Cancel</button><button type="button" class="confirm" :disabled="busy" @click="confirm">{{ busy ? 'Enabling…' : 'Enable AUTO' }}</button></footer>
    </section>
  </div>
</template>
<script setup>
import { ref, watch } from 'vue'
import { autoConfirmPayload, hasCircuitBreakerLimits, normalizedLimit, solLabel } from './autoConfirmLimits.js'

const props = defineProps({
  open: Boolean,
  busy: Boolean,
  error: { type: String, default: '' },
  currentDelay: { type: Number, default: 1000 },
  currentMaxPositions: { type: Number, default: 0 },
  currentProfitLamports: { type: Number, default: 0 },
  currentLossLamports: { type: Number, default: 0 },
})
const emit = defineEmits(['cancel', 'confirm'])
const solStep = 1_000_000 // 0.001 SOL
const maxSolLamports = 100_000_000_000 // 100 SOL
const delay = ref(1000)
const breakerEnabled = ref(false)
const maxPositions = ref(0)
const profitLamports = ref(0)
const lossLamports = ref(0)

watch(() => props.open, open => {
  if (!open) return
  delay.value = normalizedLimit(props.currentDelay, 600000, 1000)
  maxPositions.value = normalizedLimit(props.currentMaxPositions, 1000)
  profitLamports.value = normalizedLimit(props.currentProfitLamports, maxSolLamports)
  lossLamports.value = normalizedLimit(props.currentLossLamports, maxSolLamports)
  breakerEnabled.value = hasCircuitBreakerLimits(maxPositions.value, profitLamports.value, lossLamports.value)
})

function toggleBreaker() {
  if (props.busy) return
  breakerEnabled.value = !breakerEnabled.value
  // A newly enabled breaker needs an actual threshold, not three zeroes.
  if (breakerEnabled.value && !hasCircuitBreakerLimits(maxPositions.value, profitLamports.value, lossLamports.value)) {
    maxPositions.value = 2
  }
}
function confirm() {
  emit('confirm', autoConfirmPayload({
    delayMs: delay.value,
    breakerEnabled: breakerEnabled.value,
    maxPositions: maxPositions.value,
    profitLamports: profitLamports.value,
    lossLamports: lossLamports.value,
  }))
}
</script>
<style scoped>
.overlay{position:fixed;inset:0;z-index:11000;background:#000c;display:grid;place-items:center;padding:16px;backdrop-filter:blur(8px)}
.dialog{width:min(420px,100%);max-height:calc(100dvh - 32px);overflow:auto;border:1px solid #2d3d4c;border-radius:12px;padding:16px;background:#0b1015;color:#e2e8f0;box-shadow:0 20px 60px #000c}
header,footer,.stepper,.breaker-heading{display:flex;align-items:center;justify-content:space-between;gap:10px}
header strong{font-size:16px;color:#fff}header button{border:0;background:none;color:#94a3b8;font-size:24px;cursor:pointer}
p{font-size:12px;line-height:1.5;color:#aebdcc}.warning{color:#fcd34d}
.explain,.breaker{padding:12px;border:1px solid #263340;border-radius:8px;background:#111a23;margin:14px 0}
.explain strong,.breaker strong{font-size:12px}.explain p,.breaker p{margin:6px 0 0}
.breaker-heading>div{display:flex;align-items:center;gap:9px}.breaker-state{font-size:11px;color:#94a3b8}
.switch{width:44px;height:26px;flex-shrink:0;padding:3px;border:1px solid #435367;border-radius:99px;background:#283340;display:flex;align-items:center;cursor:pointer;transition:background .15s}
.switch[aria-checked=true]{background:#166534;border-color:#22c55e;justify-content:flex-end}
.switch-thumb{height:18px;width:18px;background:#e2e8f0;border-radius:50%;box-shadow:0 1px 3px #0005}
.switch:focus-visible,.stepper button:focus-visible{outline:2px solid #86efac;outline-offset:3px}
label{display:block;font-size:12px;font-weight:700;margin:12px 0 7px}
.breaker-controls{margin-top:-5px}
.stepper{border:1px solid #304052;border-radius:8px;overflow:hidden;min-height:40px}
.stepper button{width:54px;min-width:54px;height:40px;border:0;background:#132138;color:#dbeafe;font-size:22px;cursor:pointer}
.stepper output{font-variant-numeric:tabular-nums;font-weight:700;text-align:center}
small{display:block;margin-top:6px;color:#718096;font-size:10px}.breaker-note{margin-top:14px;line-height:1.4}.error{color:#fca5a5}
footer{justify-content:flex-end;margin-top:20px}footer button{min-height:36px;padding:0 14px;border:1px solid #304052;border-radius:8px;background:#111923;color:#cbd5e1;font-weight:700}
footer .confirm{border-color:#22c55e;background:#103222;color:#86efac}button:disabled{opacity:.5;cursor:default}
</style>
